import type { AuthUser } from "../types/auth";
import { FORM_IDS, PROCESS_IDS } from "../lib/permissionCatalog";
import { hasPermission } from "../lib/permissions";
import {
  getGuideTourRouteStartStepIndex as getPackageGuideTourRouteStartStepIndex,
  getGuideTourStartStepIndex as getPackageGuideTourStartStepIndex,
  getResolvedGuideTourModules,
} from "../../packages/guide-tour/src";

import type {
  GuideTourModule,
  GuideTourModuleConfig,
  GuideTourModuleDefinition,
  GuideTourPermission,
  GuideTourStep,
} from "./types";

export const GUIDE_TOUR_MODULES: GuideTourModuleDefinition[] = [
  { key: "requisition", label: "Requisition" },
  { key: "purchaseOrder", label: "Purchase Order" },
  { key: "rfq", label: "RFQs" },
  { key: "vendor", label: "Vendors" },
  { key: "item", label: "Items" },
  { key: "invoice", label: "Invoices" },
  { key: "budgetPlan", label: "Budget" },
  { key: "contract", label: "Contracts" },
  { key: "accountCode", label: "Account Codes" },
  { key: "delegation", label: "Delegations" },
  { key: "audit", label: "Audit" },
  { key: "approvalChain", label: "Approval Chains" },
  { key: "exchangeRate", label: "Exchange Rates" },
];

export const GUIDE_TOUR_CONFIG: Record<GuideTourModule, GuideTourModuleConfig> = {
  requisition: {
    label: "Requisition",
    steps: [
      {
        id: "requisition-register-overview",
        route: "/requisitions",
        target: '[data-tour-id="requisitions-page"]',
        title: "Requisition register",
        content:
          "Review vessel and office requisitions here before opening the full detail workflow.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-create-entry",
        route: "/requisitions",
        target: '[data-tour-id="requisitions-create-link"]',
        title: "Create a requisition",
        content:
          "Start spare, store, emergency, or service requisitions from this action entry point.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-filter-panel",
        route: "/requisitions",
        target: '[data-tour-id="requisitions-filters"]',
        title: "Filter active queues",
        content:
          "Narrow the register by status, requisition type, and origin so the queue stays manageable.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-create-page-overview",
        route: "/requisitions/create",
        routeLabel: "Create Requisition",
        target: '[data-tour-id="requisitions-create-page"]',
        title: "Create requisition workspace",
        content:
          "Set the requisition header here before adding lines. Crew users stay in vessel mode for spare or store requests, while office users can also select vessel context for office-originated requisitions.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-create-line-items",
        route: "/requisitions/create",
        routeLabel: "Create Requisition",
        target: '[data-tour-id="requisitions-create-line-items"]',
        title: "Build the requisition lines",
        content:
          "This section changes with the requisition type. Store requests use the catalog selector, spare requests require a vessel component plus linked items, and service requests capture the line details manually.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-create-actions",
        route: "/requisitions/create",
        routeLabel: "Create Requisition",
        target: '[data-tour-id="requisitions-create-actions"]',
        title: "Save draft or submit",
        content:
          "Save Draft keeps the requisition editable, while Save and Submit moves it into the next workflow gate. Junior vessel officers route to HOD review first, and HOD or office users submit directly to the office queue.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
        },
      },
      {
        id: "requisition-category-review-selector",
        routePattern: "/requisitions/:id/review",
        routeLabel: "Requisition Review",
        target: '[data-tour-id="category-review-accept-selector"]',
        title: "Select accepted items",
        content:
          "Use one multiselect to choose the items that should move into the Accepted table. Each selected item keeps its requested quantity by default, you can adjust that review quantity directly inside the dropdown row, and any pending item left out of the selection moves into the Rejected table when you stage the review.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
          processes: [PROCESS_IDS.REQUISITION_CATEGORY_REVIEW],
        },
      },
      {
        id: "requisition-category-review-accepted-table",
        routePattern: "/requisitions/:id/review",
        routeLabel: "Requisition Review",
        target: '[data-tour-id="category-review-accepted-table"]',
        title: "Review committed acceptances",
        content:
          "Accepted lines stay in the same paginated table, showing requested versus review quantity, and you can still edit quantities or move a line back to rejection from here.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
          processes: [PROCESS_IDS.REQUISITION_CATEGORY_REVIEW],
        },
      },
      {
        id: "requisition-category-review-finalize",
        routePattern: "/requisitions/:id/review",
        routeLabel: "Requisition Review",
        target: '[data-tour-id="category-review-finalize-actions"]',
        title: "Finalize or return the requisition",
        content:
          "After staging moves every line into Accepted or Rejected, finish with Accept Selected for mixed or accepted outcomes, or Reject All to return a fully rejected requisition for revision.",
        permission: {
          forms: [FORM_IDS.REQUISITIONS],
          processes: [PROCESS_IDS.REQUISITION_CATEGORY_REVIEW],
        },
      },
    ],
  },
  purchaseOrder: {
    label: "Purchase Order",
    steps: [
      {
        id: "purchase-order-register-overview",
        route: "/purchase-orders",
        target: '[data-tour-id="purchase-orders-page"]',
        title: "Purchase-order register",
        content:
          "Move from requisitions into approved purchasing activity without leaving the main workflow shell.",
        permission: {
          forms: [FORM_IDS.PURCHASE_ORDERS],
        },
      },
      {
        id: "purchase-order-office-controls",
        route: "/purchase-orders",
        target: '[data-tour-id="purchase-orders-filters"]',
        title: "Office register controls",
        content:
          "Office roles can focus the register, switch working views, and isolate the approvals they own.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
        },
      },
      {
        id: "purchase-order-create-page-overview",
        route: "/purchase-orders/create",
        routePattern: "/purchase-orders/create",
        routeLabel: "Create Purchase Order",
        target: '[data-tour-id="purchase-orders-create-page"]',
        title: "Create purchase-order workspace",
        content:
          "Use this page to turn one awarded RFQ into one or more vendor-specific purchase orders. Start by confirming the awarded RFQ, then work through the grouped vendor rows below.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-create-rows",
        route: "/purchase-orders/create",
        routePattern: "/purchase-orders/create",
        routeLabel: "Create Purchase Order",
        target: '[data-tour-id="purchase-orders-create-rows"]',
        title: "Commit vendor purchase-order rows",
        content:
          "Each row groups awarded items by vendor and currency. Complete the commercial fields, commit the ready rows, and leave incomplete vendors in draft until they are ready.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-create-actions",
        route: "/purchase-orders/create",
        routePattern: "/purchase-orders/create",
        routeLabel: "Create Purchase Order",
        target: '[data-tour-id="purchase-orders-create-actions"]',
        title: "Create the committed purchase orders",
        content:
          "Submit only after every required vendor row is committed. The page will create one purchase order per committed vendor row from the selected awarded RFQ.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-direct-create-page-overview",
        route: "/purchase-orders/create-direct",
        routePattern: "/purchase-orders/create-direct",
        routeLabel: "Create Direct Purchase Order",
        target: '[data-tour-id="purchase-orders-direct-create-page"]',
        title: "Create direct purchase-order workspace",
        content:
          "Use the direct-create flow when the office needs a purchase order without going through requisition and RFQ sourcing. Complete the commercial header and supporting source details first.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-direct-create-line-items",
        route: "/purchase-orders/create-direct",
        routePattern: "/purchase-orders/create-direct",
        routeLabel: "Create Direct Purchase Order",
        target: '[data-tour-id="purchase-orders-direct-create-line-items"]',
        title: "Build direct purchase-order lines",
        content:
          "Choose store, spare, or service rows here, then complete quantity, price, and remarks for every direct line before submission.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-direct-create-actions",
        route: "/purchase-orders/create-direct",
        routePattern: "/purchase-orders/create-direct",
        routeLabel: "Create Direct Purchase Order",
        target: '[data-tour-id="purchase-orders-direct-create-actions"]',
        title: "Submit the direct purchase order",
        content:
          "Create the direct purchase order only after the vessel scope, source document, vendor, pricing, and direct line items are fully complete.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.PURCHASE_ORDERS],
          processes: [PROCESS_IDS.PO_CREATE],
        },
      },
      {
        id: "purchase-order-tracker-view",
        route: "/purchase-orders?view=tracker",
        target: '[data-tour-id="purchase-orders-table"]',
        title: "Tracker-specific purchase-order view",
        content:
          "This tracker route keeps delivery-focused purchase orders visible without starting the full office register flow again.",
        permission: {
          forms: [FORM_IDS.TRACKER],
        },
      },
    ],
  },
  rfq: {
    label: "RFQs",
    steps: [
      {
        id: "rfq-register-overview",
        route: "/rfqs",
        target: '[data-tour-id="rfqs-page"]',
        title: "RFQ register",
        content:
          "Follow the office RFQ queue from draft preparation through quote evaluation and award.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-create-entry",
        route: "/rfqs",
        target: '[data-tour-id="rfqs-create-link"]',
        title: "Create an RFQ",
        content:
          "Office buyers can raise a new RFQ here when a requisition is ready for vendor pricing.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-create-page-overview",
        route: "/rfqs/create",
        routePattern: "/rfqs/create",
        routeLabel: "Create RFQ",
        target: '[data-tour-id="rfq-create-page"]',
        title: "Create RFQ workspace",
        content:
          "Use this page to prepare one or more vendor quote requests from an accepted requisition before sending them into the response workflow.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-create-requisition",
        route: "/rfqs/create",
        routePattern: "/rfqs/create",
        routeLabel: "Create RFQ",
        target: '[data-tour-id="rfq-create-requisition"]',
        title: "Select the accepted requisition",
        content:
          "Start by choosing the accepted requisition. When the page is opened from a requisition, this field stays locked to preserve the sourcing handoff.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-create-rows",
        route: "/rfqs/create",
        routePattern: "/rfqs/create",
        routeLabel: "Create RFQ",
        target: '[data-tour-id="rfq-create-rows"]',
        title: "Build RFQ rows",
        content:
          "Create separate RFQ rows for accepted items, invited vendors, response deadlines, delivery ports, and buyer remarks. Commit each ready row before submitting.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-create-actions",
        route: "/rfqs/create",
        routePattern: "/rfqs/create",
        routeLabel: "Create RFQ",
        target: '[data-tour-id="rfq-create-actions"]',
        title: "Create the RFQs",
        content:
          "Submit only after every ready row is committed. Each committed row creates a separate RFQ for vendor response tracking.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-filter-panel",
        route: "/rfqs",
        target: '[data-tour-id="rfqs-filters"]',
        title: "Filter RFQ stages",
        content:
          "Focus the queue by workflow status so sourcing and follow-up activity stays visible.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-register-table",
        route: "/rfqs",
        target: '[data-tour-id="rfqs-table"]',
        title: "Review vendor response timing",
        content:
          "Use the RFQ table to open each sourcing package and track deadlines, ports, and vessel context.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-overview",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-page"]',
        title: "Quotation comparison workspace",
        content:
          "Use this page to review RFQ items in the matrix, compare vendor columns, check one vendor per item, and record the final awards.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-item-selector",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-table"]',
        title: "Review comparison items",
        content:
          "All RFQ lines appear directly in the matrix. The Items columns show RFQ quantity, editable award quantity for the checked vendor, and requisition item remarks.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-table",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-table"]',
        title: "Choose vendor awards",
        content:
          "Each RFQ item appears as a matrix row with vendor Unit Price and Total Price subcolumns, line-level lowest indicators, quoted-currency and USD-equivalent prices, quote remarks, selection reason, and quantity-deviation reason. Checking one vendor on a line disables the other vendor checkboxes for that line.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-lowest-vendor",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-table"]',
        title: "Review lowest indicators",
        content:
          "Lowest line badges mark the least quoted vendor for each RFQ item, and the vendor header marks the lowest total using the existing comparison calculations.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-award-workflow",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-table"]',
        title: "Complete award rows",
        content:
          "Complete the award quantity, selection reason, and quantity-deviation reason directly in the checked vendor cells. Large RFQs paginate through the matrix without losing selected vendor drafts.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-comparison-submit",
        routePattern: "/rfqs/:id/comparison",
        routeLabel: "Quotation Comparison",
        target: '[data-tour-id="rfq-comparison-submit"]',
        title: "Record the award decision",
        content:
          "Record Awards saves the checked item-vendor rows using the existing RFQ award payload after the comparison authority finalizes quantities and reasons.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
        },
      },
      {
        id: "rfq-response-entry-overview",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-page"]',
        title: "Quotation response workspace",
        content:
          "This screen pivots RFQ items vertically and invited vendors horizontally so buyers can complete every vendor response from one matrix.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-import",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-import"]',
        title: "Import each vendor workbook",
        content:
          "Use the workbook import control inside a vendor header to load the completed Excel template, preview any warnings, and stage the vendor response before saving.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-vendor-metadata",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-vendor-metadata"]',
        title: "Capture vendor-level quote details",
        content:
          "Validity end date, quote date, vendor quote reference, and general remarks stay once per vendor inside the header instead of repeating on every item row.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-matrix",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-matrix"]',
        title: "Fill item pricing across vendors",
        content:
          "Each RFQ item stays on its own row while every vendor contributes grouped columns for unit price, lead time, currency, and item remarks.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-currency",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-currency-field"]',
        title: "Search compact currency codes",
        content:
          "Currency fields stay narrow by showing only the selected code, while the searchable dropdown still exposes the code and currency name from master currency.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-pagination",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-pagination"]',
        title: "Page through longer RFQs",
        content:
          "When an RFQ has more than ten items, use the pagination controls to move through batches without losing the vendor drafts already staged in the matrix.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
      {
        id: "rfq-response-entry-save",
        routePattern: "/rfqs/:id/responses",
        routeLabel: "Quotation Responses",
        target: '[data-tour-id="rfq-responses-save-actions"]',
        title: "Save every vendor response together",
        content:
          "Save commits every changed vendor header and item quote block in one action, including workbook imports that have already been reviewed on this screen.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.RFQS],
          processes: [PROCESS_IDS.RFQ_MANAGE],
        },
      },
    ],
  },
  vendor: {
    label: "Vendors",
    steps: [
      {
        id: "vendor-register-overview",
        route: "/vendors",
        target: '[data-tour-id="vendors-page"]',
        title: "Vendor register",
        content:
          "Search approved and pending vendors from a single office-side supplier register.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
        },
      },
      {
        id: "vendor-create-entry",
        route: "/vendors",
        target: '[data-tour-id="vendors-create-link"]',
        title: "Add a vendor",
        content:
          "Create a new vendor record here when the purchase workflow needs a supplier not yet onboarded.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-filter-panel",
        route: "/vendors",
        target: '[data-tour-id="vendors-filters"]',
        title: "Refine supplier search",
        content:
          "Filter by operational status, geography, equipment tags, and category tags before opening details.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
        },
      },
      {
        id: "vendor-register-table",
        route: "/vendors",
        target: '[data-tour-id="vendors-table"]',
        title: "Open vendor records",
        content:
          "The vendor register keeps the working list of suppliers, contact emails, and access into each profile.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
        },
      },
      {
        id: "vendor-create-page-overview",
        route: "/vendors/create",
        routePattern: "/vendors/create",
        routeLabel: "Create Vendor",
        target: '[data-tour-id="vendors-create-page"]',
        title: "Create vendor workspace",
        content:
          "Capture the supplier's trading profile here, including company details, payment terms, currency preference, category tags, and equipment coverage before saving the record.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-create-contacts",
        route: "/vendors/create",
        routePattern: "/vendors/create",
        routeLabel: "Create Vendor",
        target: '[data-tour-id="vendors-create-contacts"]',
        title: "Capture the primary contact",
        content:
          "Use this section to record the main supplier contact that buyers will use during RFQ, purchase-order, and follow-up communication.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-create-actions",
        route: "/vendors/create",
        routePattern: "/vendors/create",
        routeLabel: "Create Vendor",
        target: '[data-tour-id="vendors-create-actions"]',
        title: "Save the vendor record",
        content:
          "Save after the core profile, contact details, and trading coverage are complete. The vendor code is generated automatically when the new supplier is created.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-edit-page-overview",
        routePattern: "/vendors/:id/edit",
        routeLabel: "Edit Vendor",
        target: '[data-tour-id="vendors-edit-page"]',
        title: "Edit vendor workspace",
        content:
          "Use this page to update an existing supplier profile while keeping its current status, trade terms, and linked reference data in view.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-edit-contacts",
        routePattern: "/vendors/:id/edit",
        routeLabel: "Edit Vendor",
        target: '[data-tour-id="vendors-edit-contacts"]',
        title: "Maintain contact details",
        content:
          "Adjust the current primary contact details here when the supplier's commercial or operational contact information changes.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
      {
        id: "vendor-edit-actions",
        routePattern: "/vendors/:id/edit",
        routeLabel: "Edit Vendor",
        target: '[data-tour-id="vendors-edit-actions"]',
        title: "Save vendor updates",
        content:
          "Save changes only after the revised supplier profile is ready, because this updates the live vendor record used by RFQ and purchase-order workflows.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.VENDORS],
          processes: [PROCESS_IDS.VENDOR_MANAGE],
        },
      },
    ],
  },
  item: {
    label: "Items",
    steps: [
      {
        id: "item-register-overview",
        route: "/items",
        target: '[data-tour-id="items-page"]',
        title: "Item register",
        content:
          "Use the item register to search the active office item master before creating or updating store items.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
        },
      },
      {
        id: "item-create-entry",
        route: "/items",
        target: '[data-tour-id="items-create-link"]',
        title: "Add an item",
        content:
          "Users with item-manage access can open the create form here when a new store item must be added to the master list.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-filter-panel",
        route: "/items",
        target: '[data-tour-id="items-filters"]',
        title: "Filter the item register",
        content:
          "Filter by IMPA code or name, unit, and category so the register stays manageable before editing an item.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
        },
      },
      {
        id: "item-register-table",
        route: "/items",
        target: '[data-tour-id="items-table"]',
        title: "Review item rows",
        content:
          "The table shows the current item master with category, type, unit, and edit access for users allowed to maintain items.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
        },
      },
      {
        id: "item-create-page-overview",
        route: "/items/create",
        routePattern: "/items/create",
        routeLabel: "Create Item",
        target: '[data-tour-id="items-create-page"]',
        title: "Create item workspace",
        content:
          "Register a new store item here by completing the IMPA code, item name, unit, manufacturer, and category details before saving.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-create-category",
        route: "/items/create",
        routePattern: "/items/create",
        routeLabel: "Create Item",
        target: '[data-tour-id="items-create-category"]',
        title: "Select or create a category",
        content:
          "Use this category field to pick an existing item category or create a new one when the current item does not fit an existing code.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-create-actions",
        route: "/items/create",
        routePattern: "/items/create",
        routeLabel: "Create Item",
        target: '[data-tour-id="items-create-actions"]',
        title: "Save the new item",
        content:
          "Save after the item identity, unit, and category details are complete so the new row appears in the item register.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-edit-page-overview",
        routePattern: "/items/:id/edit",
        routeLabel: "Edit Item",
        target: '[data-tour-id="items-edit-page"]',
        title: "Edit item workspace",
        content:
          "Use this page to maintain the live item master entry while keeping the current item identity visible during the edit.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-edit-category",
        routePattern: "/items/:id/edit",
        routeLabel: "Edit Item",
        target: '[data-tour-id="items-edit-category"]',
        title: "Update the category mapping",
        content:
          "Adjust the category here when the item should move to a different existing category or when a new category must be created for it.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
      {
        id: "item-edit-actions",
        routePattern: "/items/:id/edit",
        routeLabel: "Edit Item",
        target: '[data-tour-id="items-edit-actions"]',
        title: "Save item updates",
        content:
          "Save changes only after the item details are ready, because this updates the live item master used by requisition and purchasing workflows.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.ITEMS],
          processes: [PROCESS_IDS.ITEM_MANAGE],
        },
      },
    ],
  },
  invoice: {
    label: "Invoices",
    steps: [
      {
        id: "invoice-register-overview",
        route: "/invoices",
        target: '[data-tour-id="invoices-page"]',
        title: "Invoice register",
        content:
          "Monitor invoice verification, approval, and payment activity from the finance-side register.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.INVOICES],
        },
      },
      {
        id: "invoice-capture-queue",
        route: "/invoices",
        target: '[data-tour-id="invoices-capture-queue"]',
        title: "Capture invoice-ready purchase orders",
        content:
          "Accounts verification users can jump straight into invoice capture for delivered and invoice-eligible POs.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.INVOICES],
          processes: [PROCESS_IDS.INVOICE_VERIFY],
        },
      },
      {
        id: "invoice-filter-panel",
        route: "/invoices",
        target: '[data-tour-id="invoices-filters"]',
        title: "Filter verification queues",
        content:
          "Use invoice and match statuses together to isolate exceptions before opening the invoice detail.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.INVOICES],
        },
      },
      {
        id: "invoice-register-table",
        route: "/invoices",
        target: '[data-tour-id="invoices-table"]',
        title: "Audit invoice outcomes",
        content:
          "The invoice table highlights match status, vendor context, and links into the verification workflow.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.INVOICES],
        },
      },
    ],
  },
  budgetPlan: {
    label: "Budget",
    steps: [
      {
        id: "budget-plan-register-overview",
        route: "/budget/plan",
        target: '[data-tour-id="budget-plans-page"]',
        title: "Budget-plan register",
        content:
          "Review vessel budget plans here before moving into worksheets, approvals, and activation steps.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.BUDGETS],
        },
      },
      {
        id: "budget-plan-create-entry",
        route: "/budget/plan",
        target: '[data-tour-id="budget-plans-create-link"]',
        title: "Create a budget plan",
        content:
          "Budget managers can start a new annual or project budget plan directly from this action group.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.BUDGETS],
          processes: [PROCESS_IDS.BUDGET_MANAGE],
        },
      },
      {
        id: "budget-plan-filter-panel",
        route: "/budget/plan",
        target: '[data-tour-id="budget-plans-filters"]',
        title: "Filter planning scope",
        content:
          "Filter by vessel, year, type, and lifecycle status before reviewing plans in detail.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.BUDGETS],
        },
      },
      {
        id: "budget-plan-register-table",
        route: "/budget/plan",
        target: '[data-tour-id="budget-plans-table"]',
        title: "Move into plan actions",
        content:
          "Open details, worksheets, submissions, and approvals from the live budget-plan register.",
        permission: {
          userTypes: ["office"],
          forms: [FORM_IDS.BUDGETS],
        },
      },
    ],
  },
  contract: {
    label: "Contracts",
    steps: [
      {
        id: "contract-register-overview",
        route: "/contracts",
        target: '[data-tour-id="contracts-page"]',
        title: "Contract register",
        content:
          "Review the contract register, approval queue, and blanket-PO conversion status from this office workspace.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_VIEW],
        },
      },
      {
        id: "contract-filter-panel",
        route: "/contracts",
        target: '[data-tour-id="contracts-filters"]',
        title: "Filter active contract queues",
        content:
          "Use status and approval-queue controls to narrow the register before opening a contract detail or draft edit.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_VIEW],
        },
      },
      {
        id: "contract-create-entry",
        route: "/contracts",
        target: '[data-tour-id="contracts-create-link"]',
        title: "Create a contract",
        content:
          "Users who can manage contracts can open the signed-agreement entry flow here before blanket PO conversion.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-register-table",
        route: "/contracts",
        target: '[data-tour-id="contracts-table"]',
        title: "Open contract records",
        content:
          "The contract table keeps draft, pending, active, and terminated records together with direct links into detail and draft edit pages.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_VIEW],
        },
      },
      {
        id: "contract-create-page-overview",
        route: "/contracts/create",
        routePattern: "/contracts/create",
        routeLabel: "Create Contract",
        target: '[data-tour-id="contracts-create-page"]',
        title: "Create contract workspace",
        content:
          "Register the signed agreement here by selecting the supplier, commercial terms, vessel scope, and account code before upload and creation.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-create-vessel-scope",
        route: "/contracts/create",
        routePattern: "/contracts/create",
        routeLabel: "Create Contract",
        target: '[data-tour-id="contracts-create-vessels"]',
        title: "Assign vessel scope",
        content:
          "This section decides whether the signed agreement creates one vessel contract or a shared batch across multiple vessels.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-create-rates",
        route: "/contracts/create",
        routePattern: "/contracts/create",
        routeLabel: "Create Contract",
        target: '[data-tour-id="contracts-create-rates"]',
        title: "Define contract rates",
        content:
          "Capture the contract rate rows here because these entries later seed the blanket purchase-order lines after approval.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-create-actions",
        route: "/contracts/create",
        routePattern: "/contracts/create",
        routeLabel: "Create Contract",
        target: '[data-tour-id="contracts-create-actions"]',
        title: "Create the contract record",
        content:
          "Create the contract only after the signed document, vessel scope, account code, and rate lines are fully complete.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-detail-overview",
        routePattern: "/contracts/:id",
        routeLabel: "Contract Detail",
        target: '[data-tour-id="contracts-detail-page"]',
        title: "Review contract detail",
        content:
          "Use the contract detail page to confirm the commercial summary, document reference, current status, and blanket-PO linkage.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_VIEW],
        },
      },
      {
        id: "contract-detail-workflow",
        routePattern: "/contracts/:id",
        routeLabel: "Contract Detail",
        target: '[data-tour-id="contracts-detail-workflow"]',
        title: "Run workflow actions",
        content:
          "Submission, approval, rejection, and termination controls appear here when the current contract status allows that next action.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_VIEW],
        },
      },
      {
        id: "contract-detail-blanket-po",
        routePattern: "/contracts/:id",
        routeLabel: "Contract Detail",
        target: '[data-tour-id="contracts-detail-blanket-po"]',
        title: "Convert to a blanket PO",
        content:
          "Once a contract is active, this section collects the commercial fields needed to create the single blanket purchase order from the approved rates.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-edit-page-overview",
        routePattern: "/contracts/:id/edit",
        routeLabel: "Edit Contract",
        target: '[data-tour-id="contracts-edit-page"]',
        title: "Edit draft contract workspace",
        content:
          "Only draft contracts can be edited here, while locked vessel assignment and numbering stay visible for reference.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-edit-rates",
        routePattern: "/contracts/:id/edit",
        routeLabel: "Edit Contract",
        target: '[data-tour-id="contracts-edit-rates"]',
        title: "Update the draft rates",
        content:
          "Maintain the editable rate rows here before the contract is submitted into the approval chain.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
      {
        id: "contract-edit-actions",
        routePattern: "/contracts/:id/edit",
        routeLabel: "Edit Contract",
        target: '[data-tour-id="contracts-edit-actions"]',
        title: "Save draft changes",
        content:
          "Save only after the revised commercial terms, replacement document, and draft rates are ready for the live draft record.",
        permission: {
          forms: [FORM_IDS.CONTRACTS],
          processes: [PROCESS_IDS.CONTRACT_MANAGE],
        },
      },
    ],
  },
  accountCode: {
    label: "Account Codes",
    steps: [
      {
        id: "account-code-register-overview",
        route: "/admin/account-codes",
        target: '[data-tour-id="account-codes-page"]',
        title: "Account-code management",
        content:
          "Manage the account-code hierarchy, expenditure types, and requisition controls from this finance-side admin page.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
        },
      },
      {
        id: "account-code-filter-panel",
        route: "/admin/account-codes",
        target: '[data-tour-id="account-codes-filters"]',
        title: "Filter the hierarchy",
        content:
          "Search by code or name, then narrow by department and expenditure type before editing a row.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
        },
      },
      {
        id: "account-code-create-entry",
        route: "/admin/account-codes",
        target: '[data-tour-id="account-codes-create-link"]',
        title: "Create an account code",
        content:
          "Users who manage account codes can open the standalone create page here for a new hierarchy node.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
        },
      },
      {
        id: "account-code-register-table",
        route: "/admin/account-codes",
        target: '[data-tour-id="account-codes-table"]',
        title: "Review hierarchy rows",
        content:
          "The register table keeps parent linkage, expenditure type, requisition requirement, and active status together for each code.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
        },
      },
      {
        id: "account-code-expenditure-types",
        route: "/admin/account-codes",
        target: '[data-tour-id="account-codes-expenditure-types"]',
        title: "Maintain expenditure types",
        content:
          "Expenditure types stay on the same admin page because they drive account-code classification and downstream reporting.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
        },
      },
      {
        id: "account-code-create-page-overview",
        route: "/admin/account-codes/create",
        routePattern: "/admin/account-codes/create",
        routeLabel: "Create Account Code",
        target: '[data-tour-id="account-codes-create-page"]',
        title: "Create account-code workspace",
        content:
          "Use the standalone create page when you need a new account-code record without the embedded register context.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
        },
      },
      {
        id: "account-code-create-form",
        route: "/admin/account-codes/create",
        routePattern: "/admin/account-codes/create",
        routeLabel: "Create Account Code",
        target: '[data-tour-id="account-codes-create-form"]',
        title: "Configure account-code metadata",
        content:
          "Complete the code, hierarchy parent, expenditure type, department tags, and requisition rule before creation.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
        },
      },
      {
        id: "account-code-create-actions",
        route: "/admin/account-codes/create",
        routePattern: "/admin/account-codes/create",
        routeLabel: "Create Account Code",
        target: '[data-tour-id="account-codes-create-actions"]',
        title: "Create the account code",
        content:
          "Create the new account code only after the hierarchy placement and finance metadata are correct.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
        },
      },
      {
        id: "account-code-edit-page-overview",
        routePattern: "/admin/account-codes/:id/edit",
        routeLabel: "Edit Account Code",
        target: '[data-tour-id="account-codes-edit-page"]',
        title: "Edit account-code workspace",
        content:
          "Use the standalone edit page to revise an existing account-code record while keeping its current hierarchy placement visible.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_MANAGE],
        },
      },
      {
        id: "account-code-edit-form",
        routePattern: "/admin/account-codes/:id/edit",
        routeLabel: "Edit Account Code",
        target: '[data-tour-id="account-codes-edit-form"]',
        title: "Update account-code metadata",
        content:
          "Adjust the finance metadata, requisition requirement, and department tags here before saving the live record.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_MANAGE],
        },
      },
      {
        id: "account-code-edit-actions",
        routePattern: "/admin/account-codes/:id/edit",
        routeLabel: "Edit Account Code",
        target: '[data-tour-id="account-codes-edit-actions"]',
        title: "Save account-code changes",
        content:
          "Save only after the revised code metadata and hierarchy rules are ready for the active account-code row.",
        permission: {
          forms: [FORM_IDS.ACCOUNT_CODES],
          processes: [PROCESS_IDS.ACCOUNT_CODE_MANAGE],
        },
      },
    ],
  },
  delegation: {
    label: "Delegations",
    steps: [
      {
        id: "delegation-register-overview",
        route: "/admin/delegations",
        target: '[data-tour-id="delegations-page"]',
        title: "Delegation register",
        content:
          "Review active and expired approval delegations here before opening a new temporary coverage window.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
      {
        id: "delegation-create-entry",
        route: "/admin/delegations",
        target: '[data-tour-id="delegations-create-link"]',
        title: "Create a delegation",
        content:
          "Start a new time-bounded office approval delegation from this action when the delegator will be unavailable.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
      {
        id: "delegation-register-table",
        route: "/admin/delegations",
        target: '[data-tour-id="delegations-table"]',
        title: "Monitor delegation coverage",
        content:
          "The register table shows delegator, delegate, role, validity window, and direct cancel action for active coverage.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
      {
        id: "delegation-create-page-overview",
        route: "/admin/delegations/create",
        routePattern: "/admin/delegations/create",
        routeLabel: "Create Delegation",
        target: '[data-tour-id="delegations-create-page"]',
        title: "Create delegation workspace",
        content:
          "Use the standalone create page to assign the temporary delegate, date window, and reason for the approval handoff.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
      {
        id: "delegation-create-form",
        route: "/admin/delegations/create",
        routePattern: "/admin/delegations/create",
        routeLabel: "Create Delegation",
        target: '[data-tour-id="delegations-create-form"]',
        title: "Set the coverage window",
        content:
          "Choose the delegate, start and end dates, and the business reason before creating the delegation.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
      {
        id: "delegation-create-actions",
        route: "/admin/delegations/create",
        routePattern: "/admin/delegations/create",
        routeLabel: "Create Delegation",
        target: '[data-tour-id="delegations-create-actions"]',
        title: "Create the delegation",
        content:
          "Create the delegation only after the delegate, date range, and reason are all validated for the temporary approval coverage.",
        permission: {
          forms: [FORM_IDS.DELEGATIONS],
          processes: [PROCESS_IDS.DELEGATION_MANAGE],
        },
      },
    ],
  },
  audit: {
    label: "Audit",
    steps: [
      {
        id: "audit-log-overview",
        route: "/audit",
        target: '[data-tour-id="audit-page"]',
        title: "Audit log register",
        content:
          "Use the audit register to trace procurement events by entity type, action, and date range.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
      {
        id: "audit-filter-panel",
        route: "/audit",
        target: '[data-tour-id="audit-filters"]',
        title: "Filter traceability views",
        content:
          "Filter by entity type, action, and date window to isolate the exact audit trail you need.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
      {
        id: "audit-register-table",
        route: "/audit",
        target: '[data-tour-id="audit-table"]',
        title: "Open audit entries",
        content:
          "The audit table lists the live traceability entries and keeps the export/open path available for each entity.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
      {
        id: "audit-export-overview",
        routePattern: "/audit/export",
        routeLabel: "Audit Export",
        target: '[data-tour-id="audit-export-page"]',
        title: "Audit export workspace",
        content:
          "The export page packages one entity trail into an ISM-ready detail view before PDF or Excel export.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
      {
        id: "audit-export-actions",
        routePattern: "/audit/export",
        routeLabel: "Audit Export",
        target: '[data-tour-id="audit-export-actions"]',
        title: "Export the audit trail",
        content:
          "Use these actions to download the current audit trail in PDF or Excel format, or return to the audit register.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
      {
        id: "audit-export-timeline",
        routePattern: "/audit/export",
        routeLabel: "Audit Export",
        target: '[data-tour-id="audit-export-timeline"]',
        title: "Review the audit timeline",
        content:
          "This timeline keeps the entry sequence, verification state, and readable audit details together for the selected entity.",
        permission: {
          forms: [FORM_IDS.AUDIT],
          processes: [PROCESS_IDS.AUDIT_VIEW],
        },
      },
    ],
  },
  approvalChain: {
    label: "Approval Chains",
    steps: [
      {
        id: "approval-chain-register-overview",
        route: "/admin/approval-chains",
        target: '[data-tour-id="approval-chains-page"]',
        title: "Approval-chain configuration",
        content:
          "Configure requisition and purchase approval routing per account code from this inline admin workspace.",
        permission: {
          forms: [FORM_IDS.APPROVAL_CHAINS],
          processes: [PROCESS_IDS.APPROVAL_CHAIN_MANAGE],
        },
      },
      {
        id: "approval-chain-table",
        route: "/admin/approval-chains",
        target: '[data-tour-id="approval-chains-table"]',
        title: "Review configured chain rows",
        content:
          "The main table groups PO reviewer, authorizer, approval thresholds, and payment authorities by account code.",
        permission: {
          forms: [FORM_IDS.APPROVAL_CHAINS],
          processes: [PROCESS_IDS.APPROVAL_CHAIN_MANAGE],
        },
      },
      {
        id: "approval-chain-editor",
        route: "/admin/approval-chains",
        target: '[data-tour-id="approval-chains-editor"]',
        title: "Edit one chain inline",
        content:
          "Create or revise one account-code approval chain directly inside the inline editor row without leaving the register.",
        permission: {
          forms: [FORM_IDS.APPROVAL_CHAINS],
          processes: [PROCESS_IDS.APPROVAL_CHAIN_MANAGE],
        },
      },
      {
        id: "contract-approval-chain-overview",
        route: "/admin/contract-approval-chains",
        routePattern: "/admin/contract-approval-chains",
        routeLabel: "Contract Approval Chains",
        target: '[data-tour-id="contract-approval-chains-page"]',
        title: "Contract approval-chain configuration",
        content:
          "This related admin page controls the separate role sequence used before contracts become active.",
        permission: {
          forms: [FORM_IDS.CONTRACT_CHAINS],
          processes: [PROCESS_IDS.CONTRACT_CHAIN_MANAGE],
        },
      },
      {
        id: "contract-approval-chain-table",
        route: "/admin/contract-approval-chains",
        routePattern: "/admin/contract-approval-chains",
        routeLabel: "Contract Approval Chains",
        target: '[data-tour-id="contract-approval-chains-table"]',
        title: "Review contract approval steps",
        content:
          "The contract approval-chain table shows step order, approver role, and active status for each configured step.",
        permission: {
          forms: [FORM_IDS.CONTRACT_CHAINS],
          processes: [PROCESS_IDS.CONTRACT_CHAIN_MANAGE],
        },
      },
      {
        id: "contract-approval-chain-form",
        route: "/admin/contract-approval-chains",
        routePattern: "/admin/contract-approval-chains",
        routeLabel: "Contract Approval Chains",
        target: '[data-tour-id="contract-approval-chains-form"]',
        title: "Create or edit a contract step",
        content:
          "Use the form below the table to add a new contract approval step or revise an existing one.",
        permission: {
          forms: [FORM_IDS.CONTRACT_CHAINS],
          processes: [PROCESS_IDS.CONTRACT_CHAIN_MANAGE],
        },
      },
    ],
  },
  exchangeRate: {
    label: "Exchange Rates",
    steps: [
      {
        id: "exchange-rate-register-overview",
        route: "/exchange-rates",
        target: '[data-tour-id="exchange-rates-page"]',
        title: "Exchange-rate register",
        content:
          "Review the current conversion register and stale-currency status from this office page.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-entry-link",
        route: "/exchange-rates",
        target: '[data-tour-id="exchange-rates-entry-link"]',
        title: "Open rate entry",
        content:
          "Users with rate-manage access can open the entry page here to record the current purchase conversion rate.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-register-table",
        route: "/exchange-rates",
        target: '[data-tour-id="exchange-rates-table"]',
        title: "Review current currencies",
        content:
          "The register table shows the current rate, effective date, stale status, and history link for each tracked currency.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-entry-overview",
        route: "/exchange-rates/entry",
        routePattern: "/exchange-rates/entry",
        routeLabel: "Exchange Rate Entry",
        target: '[data-tour-id="exchange-rates-entry-page"]',
        title: "Exchange-rate entry workspace",
        content:
          "Use the entry page to select a currency, record the rate to USD, and set the effective date for the office register.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-entry-form",
        route: "/exchange-rates/entry",
        routePattern: "/exchange-rates/entry",
        routeLabel: "Exchange Rate Entry",
        target: '[data-tour-id="exchange-rates-entry-form"]',
        title: "Capture the conversion data",
        content:
          "Complete the currency, rate, and effective date fields here before saving the new register row.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-entry-actions",
        route: "/exchange-rates/entry",
        routePattern: "/exchange-rates/entry",
        routeLabel: "Exchange Rate Entry",
        target: '[data-tour-id="exchange-rates-entry-actions"]',
        title: "Save the exchange rate",
        content:
          "Save only after the current currency rate and effective date are confirmed for the purchase register.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-history-overview",
        routePattern: "/exchange-rates/history/:currency",
        routeLabel: "Exchange Rate History",
        target: '[data-tour-id="exchange-rates-history-page"]',
        title: "Exchange-rate history view",
        content:
          "The history page gives a read-only timeline of prior rates for the selected currency.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
      {
        id: "exchange-rate-history-table",
        routePattern: "/exchange-rates/history/:currency",
        routeLabel: "Exchange Rate History",
        target: '[data-tour-id="exchange-rates-history-table"]',
        title: "Review historical entries",
        content:
          "Use the history table to verify prior effective dates, rates, and who entered each currency record.",
        permission: {
          forms: [FORM_IDS.EXCHANGE_RATES],
          processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
        },
      },
    ],
  },
};

function hasGuideTourPermission(
  user: AuthUser | null | undefined,
  permission: GuideTourPermission | undefined,
) {
  if (!user) {
    return false;
  }

  if (!permission) {
    return true;
  }

  if (permission.userTypes && !permission.userTypes.includes(user.type)) {
    return false;
  }

  const permissionCondition = {
    roles: permission.roles,
    forms: permission.forms,
    processes: permission.processes,
  };

  if (
    !permissionCondition.roles &&
    !permissionCondition.forms &&
    !permissionCondition.processes
  ) {
    return true;
  }

  return hasPermission(user, permissionCondition);
}

export function getGuideTourStepsForModule(
  user: AuthUser | null | undefined,
  module: GuideTourModule,
): GuideTourStep[] {
  if (!user) {
    return [];
  }

  return GUIDE_TOUR_CONFIG[module].steps.filter((step) =>
    hasGuideTourPermission(user, step.permission),
  );
}

export function getGuideTourStartStepIndex(
  user: AuthUser | null | undefined,
  module: GuideTourModule,
  pathname: string,
  search = "",
): number {
  const steps = getGuideTourStepsForModule(user, module);
  return getPackageGuideTourStartStepIndex(steps, pathname, search);
}

export function getGuideTourRouteStartStepIndex(
  user: AuthUser | null | undefined,
  module: GuideTourModule,
  pathname: string,
  search = "",
): number | null {
  const steps = getGuideTourStepsForModule(user, module);
  return getPackageGuideTourRouteStartStepIndex(steps, pathname, search);
}

export function getGuideTourModulesForUser(user: AuthUser | null | undefined) {
  if (!user) {
    return [];
  }

  return getResolvedGuideTourModules(GUIDE_TOUR_MODULES, (module) =>
    getGuideTourStepsForModule(user, module),
  );
}
